/* eslint-disable */
import { API_URL } from '../config';
import axios from 'axios';


const api = axios.create({ baseURL: API_URL, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  response => response.data,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login';
    }
    const data = error.response?.data;
    if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
      throw { error: 'Server unavailable. Please try again.' };
    }
    throw data || { error: error.message || 'Request failed' };
  }
);

export const authAPI = {
  login:              (username, password, organization_name) => api.post('/api/login', { username, password, organization_name }),
  changePassword:     (currentPassword, newPassword) => api.post('/api/auth/change-password', { currentPassword, newPassword }),
  updateEmail:        (email, currentPassword) => api.put('/api/auth/update-email', { email, currentPassword }),
  register:           (username, password, organization_name, email) => api.post('/api/register', { username, password, organization_name, email }),
  logout:             () => api.post('/api/logout').catch(() => {}),
  getMe:              () => api.get('/api/auth/me'),
  // Password reset (public — no auth token required)
  forgotPassword:         (email) => api.post('/api/auth/forgot-password', { email }),
  validateResetToken:     (token) => api.get(`/api/auth/validate-reset-token/${token}`),
  resetPassword:          (token, newPassword) => api.post('/api/auth/reset-password', { token, newPassword }),
  // MFA
  mfaStatus:          () => api.get('/api/auth/mfa/status'),
  mfaSetup:           () => api.get('/api/auth/mfa/setup'),
  mfaConfirm:         (code) => api.post('/api/auth/mfa/confirm', { code }),
  mfaDisable:         (password) => api.post('/api/auth/mfa/disable', { password }),
  mfaVerify:          (mfa_pending_token, code) => api.post('/api/auth/mfa/verify', { mfa_pending_token, code }),
  mfaNewBackupCodes:  (password) => api.post('/api/auth/mfa/backup-codes', { password }),
  // VERIFY-001: Email verification (public verify — no auth token required)
  verifyEmail:         (token) => api.post('/api/auth/verify-email', { token }),
  resendVerification:  () => api.post('/api/auth/resend-verification'),
  // VERIFY-002: Public resend — no auth token. This is the ONLY route out of
  // an expired-verification lockout, because the authenticated resend above
  // requires a login the user can no longer complete.
  resendVerificationPublic: (identifier) => api.post('/api/auth/resend-verification-public', { username: identifier, email: identifier }),
  // LEGAL-002
  acceptLegal:          () => api.post('/api/auth/accept-legal'),
};

export const usersAPI = {
  getAll:        () => api.get('/api/users'),
  approve:       (id) => api.put(`/api/users/${id}/approve`),
  reject:        (id) => api.put(`/api/users/${id}/reject`),
  revoke:        (id) => api.put(`/api/users/${id}/revoke`),
  resetPassword: (id, newPassword) => api.put(`/api/users/${id}/reset-password`, { newPassword }),
  unlock:        (id) => api.put(`/api/users/${id}/unlock`),
  delete:        (id) => api.delete(`/api/users/${id}`)
};

export const normaliseProperty = (p) => {
  if (!p) return p;
  return {
    ...p, capitalData: p.capital_data || p.capitalData || {}, rooms: p.rooms || [], licences: p.licences || [], companyId: p.company_id || p.companyId || null,
    // BUG FIX: propertyType was never mapped from the API's snake_case
    // `property_type`, so `prop.propertyType` was undefined everywhere. The
    // edit form's lookup fell through to its default, meaning EVERY property
    // opened for editing showed "HMO (Multiple Rooms)" regardless of what it
    // actually was -- and saving then wrote 'hmo' back over a BTL or single
    // let. That also silently changed the billable unit count, since HMOs are
    // counted per room and everything else counts as one.
    // The same omission applied to the fields below.
    propertyType: p.property_type || p.propertyType || 'hmo',
    managementType: p.management_type || p.managementType || 'self',
    agencyName: p.agency_name || p.agencyName || null,
    agencyContact: p.agency_contact || p.agencyContact || null,
    agencyFeePercent: p.agency_fee_percent ?? p.agencyFeePercent ?? null,
    purchasePrice: p.purchase_price ?? p.purchasePrice ?? null,
    purchaseDate: p.purchase_date || p.purchaseDate || null,
    additionalDetails: p.additional_details || p.additionalDetails || '',
    companyName: p.company_name || p.companyName || null,
    // FEAT-002: only meaningful when ownership is Joint. null means "not set" (the app no longer invents 50),
    // so a joint property without a share can be told apart and prompted for one.
    ownershipSharePercent: p.ownership_share_percent != null ? parseFloat(p.ownership_share_percent) : null,
    managingContractorId: p.managing_contractor_id || p.managingContractorId || null,
    managingContractorName: p.managing_contractor_name || null,
    managingContractorContact: p.managing_contractor_contact || null,
    managingContractorEmail: p.managing_contractor_email || null,
    managingContractorMobile: p.managing_contractor_mobile || null,
    managingContractorLandline: p.managing_contractor_landline || null,
  };
};

export const receiptsAPI = {
  // Multipart upload — file is a File/Blob object from an <input type="file">
  scan: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/receipts/scan', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  confirm: (receiptId, lineItems) => api.post(`/api/receipts/${receiptId}/confirm`, { lineItems }),
  download: (receiptId) => api.get(`/api/receipts/${receiptId}/download`),
};

export const documentsAPI = {
  // AI-004: Extraction only — returns {docType, referenceNumber, issueDate,
  // expiryDate, description} to pre-fill the Add Document form. Does not
  // upload or create anything itself.
  scan: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/documents/scan', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const companiesAPI = {
  getAll:  () => api.get('/api/companies').then(rows => Array.isArray(rows) ? rows : []),
  create:  (company) => api.post('/api/companies', { company }),
  update:  (id, company) => api.put(`/api/companies/${id}`, { company }),
  delete:  (id) => api.delete(`/api/companies/${id}`)
};

export const propertiesAPI = {
  getAll:  () => api.get('/api/properties').then(rows => Array.isArray(rows) ? rows.map(normaliseProperty) : []),
  create:  (propertyData) => api.post('/api/properties', { property: propertyData }).then(normaliseProperty),
  update:  (id, propertyData) => api.put(`/api/properties/${id}`, { property: propertyData }).then(normaliseProperty),
  delete:  (id) => api.delete(`/api/properties/${id}`)
};

export const normalisePayment = (p) => ({
  ...p, paidDate: p.paid_date || p.paidDate, propertyId: p.property_id || p.propertyId, roomId: p.room_id || p.roomId, tenantId: p.tenant_id || p.tenantId || null
});

export const paymentsAPI = {
  getAll: () => api.get('/api/payments').then(rows => Array.isArray(rows) ? rows.map(normalisePayment) : []),
  create: (payment) => api.post('/api/payments', { payment: { propertyId: payment.propertyId || payment.property_id, roomId: payment.roomId || payment.room_id, month: payment.month, amount: payment.amount, paidDate: payment.paidDate || payment.paid_date, tenantId: payment.tenantId || payment.tenant_id || null } }).then(normalisePayment),
  update: (id, payment) => api.put(`/api/payments/${id}`, { payment: { propertyId: payment.propertyId || payment.property_id, roomId: payment.roomId || payment.room_id, month: payment.month, amount: payment.amount, paidDate: payment.paidDate || payment.paid_date, tenantId: payment.tenantId || payment.tenant_id || null } }).then(normalisePayment),
  delete: (id) => api.delete(`/api/payments/${id}`)
};

export const normaliseExpense = (e) => ({ ...e, propertyId: e.property_id || e.propertyId });

export const expensesAPI = {
  getAll:  () => api.get('/api/expenses').then(rows => Array.isArray(rows) ? rows.map(normaliseExpense) : []),
  create:  (expense) => api.post('/api/expenses', { expense: { propertyId: expense.propertyId || expense.property_id, category: expense.category, amount: expense.amount, date: expense.date, description: expense.description, paid: expense.paid } }).then(normaliseExpense),
  update:  (id, expense) => api.put(`/api/expenses/${id}`, { expense }).then(normaliseExpense),
  delete:  (id) => api.delete(`/api/expenses/${id}`)
};

export const companyExpensesAPI = {
  getAll:  () => api.get('/api/company-expenses').then(rows => Array.isArray(rows) ? rows : []),
  create:  (expense) => api.post('/api/company-expenses', { expense }),
  update:  (id, expense) => api.put(`/api/company-expenses/${id}`, { expense }),
  delete:  (id) => api.delete(`/api/company-expenses/${id}`)
};

export const prospectsAPI = {
  getAll:  () => api.get('/api/prospective-properties').then(rows => Array.isArray(rows) ? rows : []),
  create:  (prospect) => api.post('/api/prospective-properties', { property: prospect }),
  update:  (id, prospect) => api.put(`/api/prospective-properties/${id}`, { property: prospect }),
  delete:  (id) => api.delete(`/api/prospective-properties/${id}`)
};

export const projectsAPI = {
  getAll:         () => api.get('/api/projects').then(rows => Array.isArray(rows) ? rows : []),
  create:         (project) => api.post('/api/projects', { project }),
  update:         (id, project) => api.put(`/api/projects/${id}`, { project }),
  delete:         (id) => api.delete(`/api/projects/${id}`),
  getTasksByProject: (projectId) => api.get(`/api/projects/${projectId}/tasks`),
};

export const myTasksAPI = {
  // Returns all incomplete tasks assigned to the calling user across all projects
  getAll: () => api.get('/api/my-tasks').then(rows => Array.isArray(rows) ? rows : []),
};

export const calendarAPI = {
  getAll:  () => api.get('/api/calendar-tasks').then(rows => Array.isArray(rows) ? rows : []),
  create:  (task) => api.post('/api/calendar-tasks', { task }),
  update:  (id, task) => api.put(`/api/calendar-tasks/${id}`, { task }),
  delete:  (id) => api.delete(`/api/calendar-tasks/${id}`)
};

export const settingsAPI = {
  get: () => api.get('/api/settings').catch(() => ({})),
  update: (keyOrObj, value) => {
    if (typeof keyOrObj === 'string') {
      return api.put('/api/settings', { key: keyOrObj, value: String(value) });
    }
    const obj = keyOrObj;
    const entries = Object.entries(obj).filter(([k,v]) => v !== undefined && v !== null);
    if (entries.length === 0) return Promise.resolve({});
    if (entries.length === 1) {
      return api.put('/api/settings', { key: entries[0][0], value: String(entries[0][1]) });
    }
    return Promise.all(entries.map(([k,v]) => api.put('/api/settings', { key: k, value: String(v) }))).then(() => obj);
  }
};

export const notificationPrefsAPI = {
  get:    () => api.get('/api/notification-preferences'),
  update: (prefs) => api.put('/api/notification-preferences', prefs),
};

export const adminAPI = {
  getAuditLogs: () => api.get('/api/admin/audit-logs?limit=500').then(r => Array.isArray(r) ? r : (r && r.logs ? r.logs : [])),
  getMetrics:   () => api.get('/api/admin/metrics').catch(() => null),
  getSessions:  (activeOnly = false) => api.get(`/api/admin/sessions${activeOnly ? '?active=true' : ''}`).then(r => Array.isArray(r) ? r : (r && r.sessions ? r.sessions : [])),
  createUser:   (userData) => api.post('/api/admin/create-user', userData),
  resetPasswordForUser: (userId, newPassword) => api.post(`/api/admin/reset-password/${userId}`, { newPassword }),
  // VERIFY-002: Admin-triggered resend, for users who cannot receive the
  // original email at all. Org-scoped on the server.
  resendVerificationForUser: (userId) => api.post(`/api/admin/resend-verification/${userId}`),
};

export const orgDeletionAPI = {
  // ORG-DEL-001: requires the org's exact current name as confirmName, same
  // friction as billing cancellation. Server also blocks this while a
  // Stripe subscription is still active.
  getStatus:        () => api.get('/api/org/deletion-status'),
  requestDeletion:  (confirmName) => api.post('/api/org/request-deletion', { confirmName }),
  cancelDeletion:   () => api.post('/api/org/cancel-deletion'),
};

// organizationAPI removed — link-based invites retired; use adminAPI.createUser instead

export const orgMembersAPI = {
  getAll: () => api.get('/api/org/members'),
};

export const tenantsAPI = {
  getAll:             () => api.get('/api/tenants').then(rows => Array.isArray(rows) ? rows : []),
  getOne:             (id) => api.get(`/api/tenants/${id}`),
  getByTenancyRef:    (ref) => api.get(`/api/tenants/tenancy/${encodeURIComponent(ref)}`).then(rows => Array.isArray(rows) ? rows : []),
  create:             (tenant) => api.post('/api/tenants', { tenant }),
  update:             (id, tenant) => api.put(`/api/tenants/${id}`, { tenant }),
  // BUG FIX: previously took no date at all, always recording "today" on
  // the server regardless of when the tenant actually left. moveOutDate is
  // optional — omit it and this behaves exactly as before (server defaults
  // to today).
  moveOut:            (id, moveOutDate) => api.delete(`/api/tenants/${id}`, { data: moveOutDate ? { moveOutDate } : {} }),
  getPayments:        (id) => api.get(`/api/tenants/${id}/payments`).then(rows => Array.isArray(rows) ? rows : []),
  getCommunications:  (id) => api.get(`/api/tenants/${id}/communications`).then(rows => Array.isArray(rows) ? rows : []),
  logCommunication:   (id, data) => api.post(`/api/tenants/${id}/communications`, data),
  // TENANT-ERASURE-001: GDPR Article 17. Anonymises the tenant's identity in
  // place; payment history and the property/room link are retained. No
  // corresponding "undo" method exists — this is deliberately irreversible.
  erase:              (id, reason) => api.post(`/api/tenants/${id}/erase`, { reason }),
};

export const contractorsAPI = {
  getAll:   () => api.get('/api/contractors').then(rows => Array.isArray(rows) ? rows : []),
  getOne:   (id) => api.get(`/api/contractors/${id}`),
  create:   (contractor) => api.post('/api/contractors', { contractor }),
  update:   (id, contractor) => api.put(`/api/contractors/${id}`, { contractor }),
  deactivate: (id) => api.delete(`/api/contractors/${id}`),
};

export const serviceRequestsAPI = {
  getAll:       () => api.get('/api/service-requests').then(rows => Array.isArray(rows) ? rows : []),
  getOne:       (id) => api.get(`/api/service-requests/${id}`),
  create:       (sr) => api.post('/api/service-requests', { serviceRequest: sr }),
  update:       (id, sr) => api.put(`/api/service-requests/${id}`, { serviceRequest: sr }),
  cancel:       (id) => api.delete(`/api/service-requests/${id}`),
  assign:       (id, contractor_id) => api.post(`/api/service-requests/${id}/assign`, { contractor_id }),
  acceptQuote:  (id) => api.post(`/api/service-requests/${id}/accept-quote`),
  rejectQuote:  (id, rejection_comment) => api.post(`/api/service-requests/${id}/reject-quote`, { rejection_comment }),
  complete:     (id, actual_cost, notes, create_expense) => api.post(`/api/service-requests/${id}/complete`, { actual_cost, notes, create_expense }),
};

export default api;
